/* eslint-disable max-len */
import React, { useState, useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  DoughnutController,
  LineController,
  PieController,
  Title,
  Tooltip,
  Legend,
  ChartData,
} from "chart.js";
import ChartDataLabel from "chartjs-plugin-datalabels";
import { Chart } from "react-chartjs-2";
import { transformToDataSet, barChatOption } from "../../../core/helpers";
import { getFromApiFunction } from "../../../services/api/apiFunctions";
import Loading from "../../../core/components/Loading";

// import lodash
import _, { forEach, set } from "lodash";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  DoughnutController,
  LineController,
  PieController,
  Title,
  Tooltip,
  Legend,
  ChartDataLabel,
);

export const tempOptions = {
  responsive: true,
  plugins: {
    legend: {
      position: "bottom" as const,
    },
    title: {
      display: true,
      text: "Chart.js Bar Chart",
    },
    backgroundColor: "#ebab34",
  },
};

export default function BarChart(props: any) {
  const {
    graphName,
    baseEndpoint,
    statsUnitProps,
    paramsProps,
    color,
    customHeight,
    customWidth,
    dataFilterHelper,
    otherDataSet,
    stacked,
    withLegend,
    withoutRegionSelector,
    labelColor,
    targetValue,
    extra,
    timelineProps,
    className,
  } = props;

  const [chartData, setChartData] =
    useState<ChartData<"bar", (number | [number, number] | null)[], unknown>>();
  const [apiCalling, setApiCalling] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [statsUnit, setStatsUnit] = useState(statsUnitProps);
  const [params, setParams] = useState(paramsProps);
  const [timeline, setTimeline] = useState(timelineProps);

  useEffect(() => {
    setTimeline(timelineProps);
  }, [timelineProps]);

  const firstRender = useRef(true); //To check if component is mounted

  const getChartData = async () => {
    setApiCalling(true);
    setHasError(false);
    if (
      statsUnit === "commune" &&
      !(params && (params.get("departement") || params.get("pole")))
    )
      return;

    if (
      statsUnit !== "commune" &&
      params &&
      (params.get("departement") || params.get("pole"))
    )
      return;

    // const fullUrl = baseEndpoint + "/" + (withoutRegionSelector ? "" : `by_${statsUnit}/`);
    const fullUrl =
      baseEndpoint +
      "/" +
      (withoutRegionSelector ? "" : timeline ? `by_trimester/` : `by_${statsUnit}/`);
    await getFromApiFunction(fullUrl, params)
      .then((response: any) => {
        let data = dataFilterHelper ? dataFilterHelper(response.data) : response.data;

        const chartData = transformToDataSet(
          graphName,
          data,
          color,
          otherDataSet,
          targetValue,
          timeline,
        );
        setChartData(chartData);
        setApiCalling(false);
      })
      .catch((error: any) => {
        setHasError(true);
        setApiCalling(false);
      });
  };

  useEffect(() => {
    setStatsUnit(statsUnitProps);
  }, [statsUnitProps]);

  useEffect(() => {
    if (
      _.isEqual(
        Object.fromEntries(paramsProps.entries()),
        Object.fromEntries(params.entries()),
      )
    )
      return;
    setParams(paramsProps);
  }, [paramsProps]);

  const handleBarClick = (event: any, chartElements: any) => {};

  useEffect(() => {
    getChartData();
  }, [statsUnit, params, timeline]);

  return (
    <>
      {(apiCalling || hasError) && <Loading />}
      {!apiCalling && !hasError && (
        <Chart
          options={barChatOption(
            graphName,
            handleBarClick,
            stacked,
            withLegend,
            labelColor,
          )}
          height={customHeight || 300}
          width={customWidth}
          data={
            chartData as ChartData<"bar", (number | [number, number] | null)[], unknown>
          }
          type={extra.type || "bar"}
          className={className}
        />
      )}
    </>
  );
}

export function PhysicalExecutionChart(props: any) {
  const { titleFontSize, className } = props;

  const [indic, setIndic] = useState<any>();
  const [doneValue, setDoneValue] = useState(0);
  const [data, setData] = useState<any>();

  useEffect(() => {
    getFromApiFunction("indicators/global_indic/").then((response: any) => {
      setIndic(response.data);
    });
    getFromApiFunction("indicators_values/by_trimester/?indicator__code=global").then(
      (response) => {
        if (response.data.length) setDoneValue(response.data.at(-1).value);
      },
    );
  }, []);

  useEffect(() => {
    if (!doneValue || !indic) return;
    setData({
      labels: [indic.name],
      datasets: [
        {
          label: indic.name,
          data: [doneValue, indic.target_value - doneValue],
          backgroundColor: (item: any) => {
            const { dataIndex, chart } = item;
            if (dataIndex === 0) {
              const ctx = chart.ctx;
              const gradient = ctx.createLinearGradient(0, 0, 500, 0);
              gradient.addColorStop(0, "red");
              gradient.addColorStop(0.6, "yellow");
              gradient.addColorStop(1, "green");
              return gradient;
            }
            return "gray";
          },
          cutout: "85%",
          circumference: 160,
          rotation: -80,
          datalabels: {
            display: false,
          },
        },
      ],
    });
  }, [doneValue, indic]);

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: indic?.name,
        position: "bottom" as const,
        font: {
          size: titleFontSize || 24, // 🎯 Title font size in pixels
          weight: "bold", // optional
          family: "Arial", // optional
        },
      },
    },
  };

  return (
    data && (
      <Chart
        data={data as ChartData<"bar", (number | [number, number] | null)[], unknown>}
        options={chartOptions}
        type="doughnut"
        height={450}
        width={700}
        plugins={[
          {
            id: "customCanvasBackgroundColor",
            beforeDraw: (chart: any, args: any, options: any) => {
              const { ctx } = chart;
              ctx.save();
              // Write text at the center of the chart
              ctx.fillStyle = "black";
              ctx.font = "bold 100px Arial";
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              ctx.fillText(
                doneValue,
                chart.getDatasetMeta(0).data[0].x,
                chart.getDatasetMeta(0).data[0].y - 40,
              );
              // Add % in a smaller size
              ctx.font = "bold 40px Arial";
              ctx.fillText(
                "%",
                chart.getDatasetMeta(0).data[0].x + 70,
                chart.getDatasetMeta(0).data[0].y - 30,
              );
              ctx.restore();
            },
          },
        ]}
        id="globalEvolution"
        className={className}
      />
    )
  );
}

export function TEFChart(props: any) {
  const { titleFontSize, className, height } = props;

  const [indic, setIndic] = useState<any>();
  const [data, setData] = useState<any>();

  // labels are trimesters of every year from 2022 to 2026

  useEffect(() => {
    getFromApiFunction("indicators/tef/").then((response: any) => {
      setIndic(response.data);
    });
    getFromApiFunction("indicators_values/by_trimester/?indicator__code=tef").then(
      (response) => {
        // Create an object of key value from response items
        let dataByTime: any = {};
        response.data.forEach((item: any) => {
          dataByTime[item.label] = item;
        });

        const labels: any = [];
        const indicData: any = [];
        const plannedData: any = [];

        const addIndicData = function (
          period: string,
          list: any,
          key: string = "value",
        ) {
          const value = dataByTime[period];
          if (value === undefined || value === null) return;
          list.push(value[key]);
        };

        for (let year = 2022; year <= 2026; year++) {
          for (let trimester = 1; trimester <= 3; trimester++) {
            if (trimester == 1 && year === 2022) continue;
            labels.push(`T${trimester}`);
            addIndicData(`${year}${trimester}`, indicData);
            addIndicData(`${year}${trimester}`, plannedData, "total_value");
          }
          addIndicData(`${year}4`, indicData);
          addIndicData(`${year}4`, plannedData, "total_value");
          labels.push(`${year + 1}`);
        }

        const timeSpentData = labels.map((label: any, index: any) => {
          return (index * 100) / labels.length;
        });
        setData({
          labels,
          datasets: [
            {
              label: "Prévu",
              data: plannedData,
              type: "line",
              datalabels: {
                display: false,
              },
              // display y axis to the left
              yAxisID: "yLeft",
              borderColor: "rgba(255, 0, 0, 0.5)",
            },
            {
              label: "Réalisé",
              data: indicData,
              type: "line",
              datalabels: {
                display: (context: any) => {
                  return context.dataset.data.length - 1 === context.dataIndex;
                },
                anchor: "end" as const,
                align: "end" as const,
              },
              // display y axis to the left
              yAxisID: "yLeft",
              borderColor: "green",
            },
          ],
        });
      },
    );
  }, []);

  return (
    data && (
      <Chart
        data={data as ChartData<"bar", (number | [number, number] | null)[], unknown>}
        type="line"
        height={height || 500}
        width={800}
        className={className}
        options={
          // No grid
          {
            scales: {
              x: {
                grid: {
                  display: false,
                },
              },
              yLeft: {
                type: "linear",
                position: "left",
              },
            },
            plugins: {
              legend: {
                display: true,
              },
              title: {
                display: true,
                text: "Taux d'exécution financier (en %)",
                position: "bottom" as const,
                font: {
                  size: titleFontSize || 12, // 🎯 Title font size in pixels
                  weight: "bold", // optional
                  family: "Arial", // optional
                },
              },
            },
          }
        }
      />
    )
  );
}

export function ActivitiesChart(props: any) {
  const { className } = props;
  const [indic, setIndic] = useState<any>();
  const [doneValue, setDoneValue] = useState(0);
  const [data, setData] = useState<any>();

  useEffect(() => {
    getFromApiFunction("indicators/activities/").then((response: any) => {
      setIndic(response.data);
    });
    getFromApiFunction(
      "indicators_values/by_trimester/?indicator__code=activities",
    ).then((response) => {
      if (response.data.length) setDoneValue(response.data.at(-1).value);
    });
  }, []);
  useEffect(() => {
    if (!doneValue || !indic) return;
    const remainingValue = indic.target_value - doneValue;
    setData({
      labels: [indic.name],
      datasets: [
        {
          label: indic.name,
          data: [
            { label: `Terminées : ${doneValue}`, value: doneValue },
            { label: `Restantes : ${remainingValue}`, value: remainingValue },
          ],
          backgroundColor: (item: any) => {
            return item.dataIndex ? "red" : "green";
          },
          datalabels: {
            display: true,
            color: "white",
            font: { size: 15 },
          },
        },
      ],
    });
  }, [doneValue, indic]);

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: indic?.name,
        position: "bottom" as const,
        font: {
          size: 15, // 🎯 Title font size in pixels
          weight: "bold", // optional
          family: "Arial", // optional
        },
      },
      tooltip: {
        enabled: false,
      },
    },
  };

  return (
    data && (
      <Chart
        data={data as ChartData<"bar", (number | [number, number] | null)[], unknown>}
        options={chartOptions}
        type="pie"
        height={450}
        width={700}
        id="activities"
        className={className}
      />
    )
  );
}

/**
 * Display two charts showing taks states and their relation with time
 */
export function ExtendedActivitiesChart() {
  const [ongoing, setOngoing] = useState(0);
  const [done, setDone] = useState(0);
  const [late, setLate] = useState(0);
  const [data, setData] = useState<any>();
  const [lateData, setLateData] = useState<any>();
  const [indic, setIndic] = useState<any>();

  useEffect(() => {
    getFromApiFunction("indicators/activities/").then((response: any) => {
      setIndic(response.data);
    });
    getFromApiFunction(
      "indicators_values/by_trimester/?indicator__code=activities",
    ).then((response) => {
      if (response.data.length) setDone(response.data.at(-1).value);
    });
    getFromApiFunction(
      "indicators_values/by_trimester/?indicator__code=ongoing-ac",
    ).then((response) => {
      if (response.data.length) setOngoing(response.data.at(-1).value);
    });
    getFromApiFunction("indicators_values/by_trimester/?indicator__code=late-ac").then(
      (response) => {
        if (response.data.length) setLate(response.data.at(-1).value);
      },
    );
  }, []);

  useEffect(() => {
    if (!indic) return;
    const remainingValue = indic.target_value - done - ongoing;
    const colors = ["green", "blue", "red"];
    setData({
      labels: ["Terminées", "En cours", "Non démarées"],
      datasets: [
        {
          label: indic.name,
          data: [
            { label: done, value: done },
            { label: ongoing, value: ongoing },
            { label: remainingValue, value: remainingValue },
          ],
          borderColor: colors,
          backgroundColor: [
            "rgba(0, 128, 1, 0.6)",
            "rgba(54, 162, 235, 0.7)",
            "rgba(230, 0, 1, 0.6)",
          ],
          datalabels: {
            display: true,
            color: "white",
            font: { size: 15 },
          },
        },
      ],
    });
  }, [done, ongoing, indic]);

  useEffect(() => {
    if (!indic) return;
    const remainingValue = indic.target_value - done - late;
    setLateData({
      labels: ["En retard", "Dans les temps"],
      datasets: [
        {
          label: indic.name,
          data: [
            { label: late, value: late },
            { label: remainingValue, value: remainingValue },
          ],
          borderColor: ["red", "blue"],
          backgroundColor: ["rgba(255, 99, 132, 0.6)", "rgba(54, 162, 235, 0.7)"],
          datalabels: {
            display: true,
            color: "white",
            font: { size: 15 },
          },
        },
      ],
    });
  }, [done, late, indic]);

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: "bottom" as const,
      },
      title: {
        display: false,
        text: indic?.name,
        position: "bottom" as const,
        font: {
          size: 15, // 🎯 Title font size in pixels
          weight: "bold", // optional
          family: "Arial", // optional
        },
      },
      tooltip: {
        enabled: false,
      },
    },
  };

  return (
    <div className="w-full h-[300px] flex">
      {data && (
        <Chart
          data={data as ChartData<"bar", (number | [number, number] | null)[], unknown>}
          options={chartOptions}
          type="pie"
          height={450}
          width={700}
          id="extended-activities-1"
          className="m-auto"
        />
      )}
      {lateData && (
        <Chart
          data={
            lateData as ChartData<"bar", (number | [number, number] | null)[], unknown>
          }
          options={chartOptions}
          type="pie"
          height={450}
          width={700}
          id="extended-activities-2"
          className="m-auto"
        />
      )}
    </div>
  );
}

export function RiskChart() {
  const [data, setData] = useState({});
  const [abs, setAbs] = useState({});
  const [ord, setOrd] = useState<any>({});

  useEffect(() => {
    getFromApiFunction("intervention_types/risks/").then((response: any) => {
      setData(response.data.risks);
      setAbs(response.data.likelihood);
      setOrd(response.data.impact);
    });
  }, []);

  return (
    <>
      {/* <img src="./assets/straight-arrow.svg" alt="" /> */}
      <div className="w-[90%] m-auto grid grid-cols-6 text-xs px-4 auto-rows-sm mt-2">
        <span />
        <span className="col-span-5 text-center font-bold text-sm">Probabilité</span>
        <span />
        {Object.entries(abs).map(([key, label]: any) => (
          <span className="text-center">{label}</span>
        ))}
      </div>
      <div className="w-[90%] m-auto grid grid-cols-6 px-4 auto-rows-[55px]">
        <div className="row-span-5 grid grid-cols-2 auto-rows-[55px]">
          <span
            className="text-center font-bold w-2 row-span-5 text-sm"
            style={{ writingMode: "sideways-lr" }}
          >
            Gravité
          </span>
          {Object.entries(data)
            .reverse()
            .map(([impact, risks]: any, i) => (
              <div
                className="pt-[15px] text-xs"
                style={{ writingMode: "sideways-lr", transform: "rotate(30deg)" }}
              >
                {ord[impact]}
              </div>
            ))}
        </div>
        {Object.entries(data)
          .reverse()
          .map(([impact, risks]: any, i) => (
            <>
              {Object.entries(risks).map(([likelihood, value]: any, j) => {
                const sum = 4 - i + j;
                let color = "bg-[red]";
                if (sum < 2) {
                  color = "bg-[green]";
                } else if (sum < 4) {
                  color = "bg-[#93D14E]";
                } else if (sum < 5) {
                  color = "bg-[yellow]";
                } else if (sum < 7) {
                  color = "bg-[#FEBF04]";
                }
                return (
                  <div
                    className={`border-solid border-[1px] w-full text-center pt-[15px] ${color}`}
                  >
                    {value}
                  </div>
                );
              })}
            </>
          ))}
      </div>
      <div className="text-[15px] font-bold w-full text-center mt-4 font-[Arial] text-[#666666]">
        Matrice des risques
      </div>
    </>
  );
}

export function BenefsChart() {
  const [data, setData] = useState<any>();

  useEffect(() => {
    getFromApiFunction("indicators_values/benefs/").then((response: any) => {
      const { houses, companies, institutions } = response.data;

      const labels = [
        // First dataset with axis to the left
        "Ménages - Cible",
        "Ménages",
        // Second dataset with axis to the right
        "PME - Cible",
        "PME",
        "Structures - Cible",
        "Structures",
      ];
      const display = (context: any) => {
        return Boolean(context.dataset.data[context.dataIndex]);
      };
      setData({
        labels,
        datasets: [
          // Houses target value
          {
            label: "Cible",
            data: [houses.planned || 0, 0, 0, 0, 0, 0],
            datalabels: {
              display,
            },
            yAxisID: "yLeft",
            backgroundColor: "rgba(0, 128, 1, 0.6)",
          },
          // Houses men values
          {
            label: "Ménages",
            data: [0, houses.total - houses.women, 0, 0, 0, 0],
            datalabels: {
              display,
            },
            yAxisID: "yLeft",
            backgroundColor: "rgba(0, 128, 1, 0.6)",
          },
          // Houses women values
          {
            label: "Ménages - Femmes",
            data: [0, houses.women, 0, 0, 0, 0],
            datalabels: {
              display,
            },
            yAxisID: "yLeft",
            backgroundColor: "#FE8DA2",
            displayLegend: true,
          },
          // PME
          {
            label: "PME - Cible",
            data: [0, 0, companies.planned || 0, 0, 0, 0],
            datalabels: {
              display,
            },
            yAxisID: "yRight",
            backgroundColor: "#76BCEE",
          },
          {
            label: "PME",
            data: [0, 0, 0, companies.total - companies.women, 0, 0],
            datalabels: {
              display,
            },
            yAxisID: "yRight",
            backgroundColor: "#76BCEE",
          },
          {
            label: "PME - Femmes",
            data: [0, 0, 0, companies.women, 0, 0],
            datalabels: {
              display,
            },
            yAxisID: "yRight",
            backgroundColor: "#FDBF04",
          },
          // Structures
          {
            label: "Structures - Cible",
            data: [0, 0, 0, 0, institutions.planned, 0],
            datalabels: {
              display,
            },
            yAxisID: "yRight",
            backgroundColor: "rgba(204, 159, 11, 0.6)",
          },
          {
            label: "Structures",
            data: [0, 0, 0, 0, 0, institutions.total],
            datalabels: {
              display,
            },
            yAxisID: "yRight",
            backgroundColor: "rgba(204, 159, 11, 0.6)",
          },
        ],
      });
    });
  }, []);

  return (
    data && (
      <Chart
        data={data as ChartData<"bar", (number | [number, number] | null)[], unknown>}
        options={{
          scales: {
            x: {
              grid: {
                display: false,
              },
              stacked: true,
            },
            yRight: {
              type: "linear",
              position: "right",
              title: {
                display: true,
                text: "PME & Institutions",
                color: "blue",
              },
              stacked: true,
              ticks: {
                stepSize: 300, // Step size for first y axis
              },
              max: 1800,
            },
            yLeft: {
              type: "linear",
              position: "left",
              title: {
                display: true,
                text: "Ménages",
                color: "green",
              },
              stacked: true,
              ticks: {
                stepSize: 30000, // Step size for first y axis
              },
              max: 180000,
            },
          },
          plugins: {
            legend: {
              labels: {
                generateLabels(chart) {
                  // Filter out datasets with showInLegend === false
                  return ChartJS.defaults.plugins.legend.labels
                    .generateLabels(chart)
                    .filter(
                      (label: any) =>
                        chart.data.datasets[label.datasetIndex].label?.includes(
                          "Femmes",
                        ),
                    );
                },
              },
            },
            title: {
              display: true,
              text: "Bénéficiaires",
              position: "bottom" as const,
              font: {
                size: 14, // 🎯 Title font size in pixels
                weight: "bold", // optional
                family: "Arial", // optional
              },
            },
          },
        }}
        type="bar"
        height={650}
        width={800}
        id="activities"
      />
    )
  );
}
